import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, MapPin, Calendar as CalIcon, User as UserIcon, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_COLORS = {
  scheduled: "bg-primary/15 text-primary",
  in_progress: "bg-amber-500/15 text-amber-500",
  done: "bg-accent/15 text-accent",
  cancelled: "bg-destructive/15 text-destructive",
};
const STATUS_LABEL = { scheduled: "Geplant", in_progress: "Läuft", done: "Fertig", cancelled: "Abgesagt" };

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { title: "", description: "", address: "", lat: "", lng: "", assigned_to: "", scheduled_at: "", duration_minutes: 60, status: "scheduled", price: 0 };
  const [form, setForm] = useState(emptyForm);

  async function load() {
    const { data } = await api.get("/jobs"); setJobs(data);
    try { const u = await api.get("/users"); setUsers(u.data); }
    catch (err) { console.warn("Jobs: could not load users", err); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      const payload = { ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
        duration_minutes: parseInt(form.duration_minutes) || 60,
        price: parseFloat(form.price) || 0,
        assigned_to: form.assigned_to || null,
      };
      if (editing) await api.patch(`/jobs/${editing}`, payload);
      else await api.post("/jobs", payload);
      toast.success(editing ? "Einsatz aktualisiert" : "Einsatz erstellt");
      setOpen(false); setEditing(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  }

  async function remove(id) {
    if (!window.confirm("Einsatz wirklich löschen?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Gelöscht"); load();
  }

  function edit(j) {
    setEditing(j.id);
    setForm({
      title: j.title || "", description: j.description || "", address: j.address || "",
      lat: j.lat ?? "", lng: j.lng ?? "", assigned_to: j.assigned_to || "",
      scheduled_at: j.scheduled_at || "", duration_minutes: j.duration_minutes || 60,
      status: j.status || "scheduled", price: j.price || 0,
    });
    setOpen(true);
  }

  async function changeStatus(j, status) {
    await api.patch(`/jobs/${j.id}`, { status });
    load();
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="jobs-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Planung</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Einsätze</h1>
        </div>
        <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if(!o){ setEditing(null); setForm(emptyForm);} }}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-11 px-6 font-semibold" data-testid="btn-create-job"><Plus className="w-4 h-4 mr-1" /> Neuer Einsatz</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-3xl">
            <DialogHeader><DialogTitle>{editing ? "Einsatz bearbeiten" : "Neuer Einsatz"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titel</Label><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="job-title" className="rounded-full h-10 px-4" /></div>
              <div><Label>Beschreibung</Label><Textarea value={form.description} onChange={e=>setForm({...form, description:e.target.value})} data-testid="job-desc" className="rounded-2xl" /></div>
              <div><Label>Adresse</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} data-testid="job-address" className="rounded-full h-10 px-4" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Latitude</Label><Input value={form.lat} onChange={e=>setForm({...form, lat:e.target.value})} data-testid="job-lat" className="rounded-full h-10 px-4" placeholder="52.52" /></div>
                <div><Label>Longitude</Label><Input value={form.lng} onChange={e=>setForm({...form, lng:e.target.value})} data-testid="job-lng" className="rounded-full h-10 px-4" placeholder="13.40" /></div>
              </div>
              <div><Label>Datum / Uhrzeit</Label><Input type="datetime-local" value={form.scheduled_at?.slice(0,16)} onChange={e=>setForm({...form, scheduled_at:e.target.value})} data-testid="job-date" className="rounded-full h-10 px-4" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dauer (Min)</Label><Input type="number" value={form.duration_minutes} onChange={e=>setForm({...form, duration_minutes:e.target.value})} className="rounded-full h-10 px-4" /></div>
                <div><Label>Preis (€)</Label><Input type="number" value={form.price} onChange={e=>setForm({...form, price:e.target.value})} className="rounded-full h-10 px-4" /></div>
              </div>
              <div><Label>Mitarbeiter</Label>
                <Select value={form.assigned_to} onValueChange={v=>setForm({...form, assigned_to:v})}>
                  <SelectTrigger className="rounded-full h-10 px-4" data-testid="job-assignee"><SelectValue placeholder="Nicht zugewiesen" /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u=>u.role!=="customer").map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                  <SelectTrigger className="rounded-full h-10 px-4" data-testid="job-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-full" data-testid="job-save">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {["scheduled","in_progress","done","cancelled"].map(st => (
          <div key={st} className="rounded-3xl bg-secondary/40 p-4 min-h-[300px]">
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="font-heading font-semibold">{STATUS_LABEL[st]}</h3>
              <Badge variant="secondary" className="rounded-full">{jobs.filter(j=>j.status===st).length}</Badge>
            </div>
            <div className="space-y-3">
              {jobs.filter(j=>j.status===st).map(j => (
                <Card key={j.id} className="rounded-2xl p-4 border-border/60 glass hover:-translate-y-0.5 transition" data-testid={`job-card-${j.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm">{j.title}</div>
                    <Badge className={`rounded-full ${STATUS_COLORS[j.status]}`}>{STATUS_LABEL[j.status]}</Badge>
                  </div>
                  {j.address && <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{j.address}</div>}
                  {j.scheduled_at && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><CalIcon className="w-3 h-3" />{new Date(j.scheduled_at).toLocaleString('de-DE')}</div>}
                  {j.assigned_to && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><UserIcon className="w-3 h-3" />{users.find(u=>u.id===j.assigned_to)?.name || "—"}</div>}
                  {j.price > 0 && (
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="font-semibold text-accent">€ {j.price}</span>
                      {j.commission_amount > 0 && (
                        <span className="text-muted-foreground">Agentur: € {j.commission_amount?.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1 mt-3">
                    <Select value={j.status} onValueChange={(v)=>changeStatus(j,v)}>
                      <SelectTrigger className="h-8 rounded-full text-xs flex-1" data-testid={`job-status-${j.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={()=>edit(j)} data-testid={`job-edit-${j.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                    {user?.role === "admin" && <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" onClick={()=>remove(j.id)} data-testid={`job-delete-${j.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
